import {
  DragDropContext,
  Draggable,
  Droppable,
  type OnDragEndResponder,
} from "@hello-pangea/dnd";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { GripVertical, Plus, X } from "react-bootstrap-icons";
import type { DocumentSubType, Interrogatory } from "@/briefpoint-client";
import Button from "@/components/Button";
import { FormModal } from "@/components/Modals/Modal";
import { getSubTypeText } from "@/lib/utils";

type DraggableInterrogatory = Omit<Partial<Interrogatory>, "id"> & {
  id: string; // This is a number in InterrogatoryResponse
  empty: boolean; // Assigned by getEmptyInterrogatories. Looks like it's always true.
  parentNumber: number; // Assigned by index in getEmptyInterrogatories
  number: number; // Required by InterrogatoryResponse
};

function isDraggableInterrogatory(
  item: Interrogatory | DraggableInterrogatory,
): item is DraggableInterrogatory {
  return "empty" in item && item.empty === true;
}

export default function InterrogatoriesList({
  interrogatoriesList,
  interrogatoryId,
  onAdd,
  onEdit,
  onDelete,
  documentSubType,
  startingNumber,
}: {
  interrogatoriesList: Interrogatory[];
  interrogatoryId: string;
  onAdd: (index: number, text: string) => void;
  onEdit: (id: number, index: number, text?: string) => void;
  onDelete: (interrogatoryId: number) => void;
  documentSubType: DocumentSubType | undefined;
  startingNumber: number;
}) {
  const [list, setList] = useState<Interrogatory[]>([]);
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);

  /**
   * Count of "Add new" placeholder slots below each real interrogatory.
   * Key = interrogatory.number (1-based), value = how many empty rows to show after that item.
   */
  const [newItems, setNewItems] = useState<Map<number, number>>(new Map());

  useEffect(() => {
    setList(interrogatoriesList);
  }, [interrogatoriesList]);

  /** On delete, move any placeholder count from this item's position to the one above. */
  async function handleConfirmDelete() {
    if (confirmRemoveId !== null) {
      const interrogatory = list.find((i) => i.id === confirmRemoveId);
      if (interrogatory) {
        await onDelete(confirmRemoveId);
        const count = newItems.get(interrogatory.number) || 0;
        if (count > 0) {
          // Fix: update state immutably so React re-renders. Never mutate newItems (e.g. newItems.set(...)) directly.
          const next = new Map(newItems);
          next.set(interrogatory.number, 0);
          next.set(interrogatory.number - 1, count);
          setNewItems(next);
        }
        setConfirmRemoveId(null);
      }
    }
  }

  /**
   * Adjust placeholder count for a given position.
   * - addNew=false: remove one placeholder (user cancelled or deleted the slot).
   * - addNew=true: user submitted the "add" form — remove one placeholder here and add one below (next position).
   */
  function removeNewItem(number: number, addNew = false) {
    const count = newItems.get(number) || 0;
    if (addNew) {
      if (count > 0) {
        // Fix: copy Map before mutating so we don't mutate React state (newItems).
        const next = new Map(newItems);
        next.set(number, 0);
        next.set(number + 1, count - 1);
        setNewItems(next);
      }
    } else {
      if (count > 0) {
        setNewItems(new Map(newItems).set(number, count - 1)); // New Map from existing, then .set
      }
    }
  }

  /**
   * Reorder the combined list (real + placeholders) and return only real interrogatories
   * in the new order with updated 1-based numbers. newIndex is the new 1-based position
   * of the moved item (for persisting via onEdit). Dragging a placeholder is a no-op for list order.
   */
  const reorder = (
    list: (DraggableInterrogatory | Interrogatory)[],
    startIndex: number,
    endIndex: number,
  ): { orderedList: Interrogatory[]; newIndex: number } => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    const removedId = removed.id;
    if (isDraggableInterrogatory(removed)) {
      return {
        orderedList: list.filter(
          (i): i is Interrogatory => !isDraggableInterrogatory(i),
        ),
        newIndex: 0,
      };
    }
    result.splice(endIndex, 0, removed);
    const orderedItemList: Interrogatory[] = [];
    let newIndex = 0;
    let position = 0;
    for (const item of result) {
      if (!isDraggableInterrogatory(item)) {
        position += 1;
        // Fix: create new object with updated number instead of mutating item.number in place.
        // Mutating state objects prevents React from detecting changes and can leave the UI stale.
        const updated = { ...item, number: position };
        if (item.id === removedId) newIndex = position;
        orderedItemList.push(updated);
      }
    }
    return { orderedList: orderedItemList, newIndex };
  };

  const onDragEnd: OnDragEndResponder = (res) => {
    const { draggableId, destination, source } = res;
    if (!destination) return;
    if (destination.index === source.index) return;

    const sourceItem = combinedList[source.index];
    const destinationItem = combinedList[destination.index];
    // Where the item is going: use parentNumber for placeholders, number for real interrogatories.
    let parentNumber = isDraggableInterrogatory(destinationItem)
      ? destinationItem.parentNumber
      : destinationItem.number;

    // --- Dragging an "Add new" placeholder ---
    if (isDraggableInterrogatory(sourceItem)) {
      if (
        sourceItem.parentNumber !== parentNumber ||
        !isDraggableInterrogatory(destinationItem)
      ) {
        if (
          destination.index < source.index &&
          !isDraggableInterrogatory(destinationItem)
        ) {
          parentNumber = parentNumber - 1;
        }
        removeNewItem(sourceItem.parentNumber);
        // Fix: new Map(newItems).set(...) — do not mutate newItems (e.g. newItems.set(...)...at least until we can refactor this whole thing.).
        setNewItems(
          new Map(newItems).set(
            parentNumber,
            (newItems.get(parentNumber) || 0) + 1,
          ),
        );
      }
    } else {
      // --- Dragging a real interrogatory (not a placeholder when adding a new one) ---
      // Fix: work on a copy of newItems (new Map(newItems)), not the state reference.
      // Mutating newItems directly meant React sometimes didn't re-render and the list stayed out of sync.
      const newMap = new Map(newItems);
      let setNew = false;
      for (let i = 0; i < combinedList.length; i++) {
        const item = combinedList[i];
        if (isDraggableInterrogatory(item)) {
          setNew = true;
          if (source.index > destination.index) {
            // Moving up: slots between destination and source shift down (parentNumber + 1).
            if (i >= destination.index && i < source.index) {
              newMap.set(
                item.parentNumber,
                (newMap.get(item.parentNumber) ?? 0) - 1,
              );
              newMap.set(
                item.parentNumber + 1,
                (newMap.get(item.parentNumber + 1) ?? 0) + 1,
              );
            }
          } else {
            // Moving down: slots between source and destination shift up (parentNumber - 1).
            if (i > source.index && i <= destination.index) {
              newMap.set(
                item.parentNumber,
                (newMap.get(item.parentNumber) ?? 0) - 1,
              );
              newMap.set(
                item.parentNumber - 1,
                (newMap.get(item.parentNumber - 1) ?? 0) + 1,
              );
            }
          }
        }
      }
      if (sourceItem.number !== parentNumber) {
        const { orderedList, newIndex } = reorder(
          combinedList,
          source.index,
          destination.index,
        );
        setList(orderedList);
        if (setNew) {
          setNewItems(new Map(newMap)); // Pass a new Map so React sees the update.
        }
        // Fix: parent expects 0-based index and does number: index + 1; we store 1-based so pass newIndex - 1.
        onEdit(Number.parseInt(draggableId, 10), newIndex - 1);
      } else {
        if (setNew) {
          setNewItems(new Map(newMap));
        }
      }
    }
  };

  /**
   * Flat list for DragDropContext: [empties before 1st] + for each real interrogatory,
   * [item, ...empties after it]. Order and length must match what we render so
   * indices in onDragEnd line up with the Draggable list.
   * Fix: derived in useMemo from list + newItems only, so the DnD list always reflects
   * current state and stays in sync after reorders (no stale or inconsistent combinedList).
   */
  const combinedList = useMemo(() => {
    function buildEmpties(index: number): DraggableInterrogatory[] {
      const count = newItems.get(index) ?? 0;
      return Array.from({ length: count }, (_, i) => ({
        id: `${index}-${i}`,
        empty: true as const,
        parentNumber: index,
        number: i,
      }));
    }
    const result: (DraggableInterrogatory | Interrogatory)[] = [
      ...buildEmpties(0),
    ];
    for (const interrogatory of list) {
      result.push(interrogatory);
      result.push(...buildEmpties(interrogatory.number));
    }
    return result;
  }, [list, newItems]);

  return (
    <>
      {combinedList.length > 0 ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="interrogatories-list">
            {(provided) => (
              <div
                className="interrogatories-edit mt-2"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {combinedList.map((interrogatory, index) => (
                  <Draggable
                    key={interrogatory.id}
                    draggableId={`${interrogatory.id}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        ref={provided.innerRef}
                        className={clsx("selection-item", {
                          isDragging: snapshot.isDragging,
                        })}
                      >
                        {isDraggableInterrogatory(interrogatory) ? (
                          <InterrogatoryAdd
                            onAdd={async (text: string) => {
                              await onAdd(interrogatory.parentNumber, text);
                              removeNewItem(interrogatory.parentNumber, true);
                            }}
                            onDelete={() =>
                              removeNewItem(interrogatory.parentNumber)
                            }
                          />
                        ) : (
                          <InterrogatoryEdit
                            interrogatory={interrogatory}
                            isLastItem={index + 1 === combinedList.length}
                            startingId={
                              interrogatoryId === interrogatory.id.toString()
                            }
                            onAdd={() => {
                              const count =
                                newItems.get(interrogatory.number) || 0;
                              setNewItems(
                                new Map(newItems).set(
                                  interrogatory.number,
                                  count + 1,
                                ),
                              );
                            }}
                            onEdit={onEdit}
                            onDelete={() => {
                              setConfirmRemoveId(interrogatory.id);
                            }}
                            documentSubType={documentSubType}
                            startingNumber={startingNumber}
                          />
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Button
          variant="outline"
          onClick={() => {
            setNewItems(new Map(newItems).set(0, 1)); // One placeholder before first item
          }}
        >
          <Plus />
          Add new
        </Button>
      )}
      <FormModal
        title={"Are you sure?"}
        cancelText="Cancel"
        confirmText="Yes, remove"
        show={confirmRemoveId != null}
        onClose={() => setConfirmRemoveId(null)}
        onConfirm={handleConfirmDelete}
      >
        <div>
          Are you sure you want to remove this interrogatory? This cannot be
          undone.
        </div>
      </FormModal>
    </>
  );
}

export function InterrogatoryEdit({
  isLastItem,
  interrogatory,
  startingId,
  onAdd,
  onEdit,
  onDelete,
  documentSubType,
  startingNumber,
}: {
  isLastItem: boolean;
  interrogatory: Interrogatory;
  startingId: boolean;
  onAdd: () => void;
  onEdit: (id: number, index: number, text?: string) => void;
  onDelete: () => void;
  documentSubType: DocumentSubType | undefined;
  startingNumber: number;
}) {
  const [content, setContent] = useState(interrogatory.text);
  const [hoverEffect, setHoverEffect] = useState(false);

  const [isHovering, setIsHovering] = useState(false);

  const editingRef = useRef<HTMLTextAreaElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch editingRef
  useEffect(() => {
    if (startingId && editingRef.current) {
      setTimeout(() => {
        setHoverEffect(false);
      }, 1000);
      setHoverEffect(true);
      editingRef.current.focus();
    }
  }, [editingRef, startingId]);

  function handleEditingBlur() {
    if (content !== interrogatory.text && content !== "") {
      // Fix: parent expects 0-based index (number: index + 1); interrogatory.number is 1-based.
      onEdit(interrogatory.id, interrogatory.number - 1, content);
    }
  }

  function handleOnKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      setContent(interrogatory.text);
      editingRef.current?.blur();
    } else if (e.key === "Enter") {
      e.preventDefault();
      editingRef.current?.blur();
    }
  }

  const handleMouseOver = () => {
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
  };

  return (
    <div
      className={clsx("interrogatory-item mb-3", {
        selected: hoverEffect,
      })}
      key={interrogatory.id}
      onMouseOver={handleMouseOver}
      onFocus={handleMouseOver}
      onMouseOut={handleMouseOut}
      onBlur={handleMouseOut}
    >
      <div className="main-item">
        <div className="d-flex justify-content-between">
          <div className="interrogatory-title">
            {documentSubType
              ? getSubTypeText(documentSubType, "").request
              : "Question"}{" "}
            {interrogatory.number - 1 + startingNumber}
          </div>
          <div>
            <X className="remove-item" onClick={onDelete} />
          </div>
        </div>
        <div className="d-flex align-items-center">
          <GripVertical className="drag-selection-item" />
          <Form.Control
            as="textarea"
            value={content}
            ref={editingRef}
            style={{ height: "100px" }}
            onChange={(e) => setContent(e.target.value)}
            onBlur={handleEditingBlur}
            onKeyDown={handleOnKeyDown}
          />
        </div>
      </div>
      <div
        className={clsx("add-item-wrapper", {
          "hide-item": !isHovering && !isLastItem,
        })}
      >
        <div className="border-line">
          <div className="button-wrapper">
            <Button variant="outline" onClick={onAdd}>
              <Plus />
              Add new
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function InterrogatoryAdd({
  onAdd,
  onDelete,
}: {
  onAdd: (text: string) => void;
  onDelete: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const scrollToRef = useRef<HTMLDivElement>(null);
  const [content, setContent] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch onAdd
  useEffect(() => {
    // Reset state on rebuild.  Not doing this causes other list items to take over
    // the objects ID.  Doing this outside a useEffect causes an "unmounted" error
    setContent("");
    setIsSaving(false);
    if (scrollToRef.current) {
      scrollToRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [onAdd]);

  async function handleBlur() {
    if (content !== "") {
      setIsSaving(true);
      onAdd(content);
    }
  }

  function handleOnKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      ref.current?.blur();
    }
  }

  return (
    <div className="interrogatory-item showHover pb-3" ref={scrollToRef}>
      <div className="main-item">
        <div className="d-flex justify-content-between">
          <div className="interrogatory-title">Adding new item</div>
          <div>
            <X className="remove-item" onClick={onDelete} />
          </div>
        </div>
        <div className="d-flex align-items-center">
          <GripVertical className="drag-selection-item" />
          <Form.Control
            autoFocus
            className="new-item-form"
            as="textarea"
            value={content}
            ref={ref}
            onChange={(e) => setContent(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleOnKeyDown}
            disabled={isSaving}
          />
        </div>
      </div>
    </div>
  );
}
